import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useMutationData } from "@hooks/useMutationData";
import queryKeys from "@configs/react-query/query-key";
import ToastConstant from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";

const useExportFileVulnerabilityPolicy = () => {
  const { t } = useTranslation();

  // Use useMutationData to call API export
  const { mutate, isLoading, error } = useMutationData(
    queryKeys.vulnerabilityPolicyExport,
    {
      method: "GET",
      responseType: "blob",
    },
    {
      onError: (err) => {
        console.error("Mutate error:", err);
        ToastConstant(TOAST.error, t("license-policy.file-export-failed", { error: err.message || "Unknown error" }));
      },
    },
  );

  const exportFile = useCallback(async () => {
    try {
      // Call API export throught mutate
      const response = await new Promise((resolve, reject) => {
        mutate(null, {
          onSuccess: (response) => {
            resolve(response);
          },
          onError: (err) => reject(err),
        });
      });

      // Check response and response.data
      if (!response || !(response instanceof Blob)) {
        throw new Error(`Response is not a valid Blob. Received: ${typeof response}, Data: ${response}`);
      }

      // Check Content-Type
      const contentType =
        response.headers?.["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      if (
        !contentType.includes("application/octet-stream") &&
        !contentType.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
      ) {
        console.warn("Unexpected Content-Type:", contentType);
      }

      // Create Blob from response.data
      const blob = new Blob([response], {
        type: contentType,
      });

      // Create URL for file blob
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const currentTimestamp = Date.now();

      let fileName = `VULNERABILITY_POLICY_${currentTimestamp}.xlsx`;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      ToastConstant(TOAST.success, t("license-policy.file-exported-successfully"));
    } catch (err) {
      console.error("Export error:", err);
      ToastConstant(t("license-policy.file-export-failed", { error: err.message || "Unknown error" }), TOAST.error);
    }
  }, [t, mutate]);

  return { exportFile, isLoading, error };
};

export default useExportFileVulnerabilityPolicy;
